import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../generic-components/Layout';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import SubContent from '../../../generic-components/Layout/SubContent';
import {
  iFrameQueryParams,
  INVOICE_CUSTOMIZATION_INITIAL_DATA,
  ONBOARDING_VIEWS_MAP,
  STEPS_DATA,
  STEPS_TO_AVOID_PROFILE_NAVBAR,
  STEPS_TO_AVOID_SUB_MENU,
  STYLES,
  SUBMIT_TYPES,
} from './consts/consts';
import { StepSwitch } from './components/StepSwitch';
import { useInvoiceCustomizationQuery } from '../../../hooks/query/useInvoiceCustomizationQuery';
import { useStore } from '../../../store';

import { IOnboardingContext, IUpdateInvoiceData } from './types/types';
import { useUpdateInvoiceSettingsMutation } from '../../../hooks/mutations/useUpdateInvoiceSettingsMutation';
import { SnackBar } from './components/SnackBar';
import { NavChildren } from './components/NavChildren';
import { useCompleteStripeConnectionMutation } from '../../../hooks/mutations/useCompleteStripeConnectionMutation';
import { InvoicesContext } from '../contexts/InvoicesContext';
import { getInitialStepNumberFromRoutePath, validateInitialStep } from '../utils';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../constants/general';
import { FeatureAccess } from '../../AccountSettings/components/rolesAndPermissions/PermissionsList';

export const OnboardingContext = createContext<IOnboardingContext>({
  onStepSubmit: () => {},
  activeStep: ONBOARDING_VIEWS_MAP.INTRO,
});

export const InvoiceOnboarding = () => {
  const [activeStep, setActiveStep] = useState<number>(ONBOARDING_VIEWS_MAP.PRELOAD);
  const shouldShowSubMenu = useMemo(() => !STEPS_TO_AVOID_SUB_MENU.includes(activeStep), [activeStep]);
  const {
    user: { customerId },
    searchParams,
  } = useStore((state) => state);
  const [invoiceCustomData, setInvoiceCustomData] = useState<IUpdateInvoiceData>(INVOICE_CUSTOMIZATION_INITIAL_DATA);
  const [customLogoExternalId, setCustomLogoExternalId] = useState<string>('');
  const [connectionType, setConnectionType] = useState<string>('');
  const context = useContext(InvoicesContext);
  const invoicesPermission = featureAccess(FEATURE_FLAG.INVOICE, LimitedFeatureType.Invoices);

  /** Mutations * */
  const { mutateAsync: mutateCompleteStripeConnection } = useCompleteStripeConnectionMutation();

  /** Queries * */
  const {
    refetch: fetchCustomizationData,
    data: customizationData,
    isRefetching: isRefetchCustomizationData,
    isLoading: isInitialRefetchCustomizationData,
  } = useInvoiceCustomizationQuery({
    customerId,
    onSuccess: (customizationDataRes) => {
      const defColor = customizationDataRes.brandColors.filter((clr) => clr.isDefault)[0];
      const defLogo = customizationDataRes.liliLogos.filter((logo) => logo.isDefault)[0];
      const defTemplate = customizationDataRes.templates.filter((template) => template.isDefault)[0];
      if (context?.initialObStep) {
        setInvoiceCustomData((prevState) => ({
          ...prevState,
          brandColor: context.settings.brandColor,
          liliLogoId: context.settings.liliLogoId,
          templateId: defTemplate.id,
          templateVersion: defTemplate.version,
          enableAchPaymentMethod: context.settings.enableAchPaymentMethod,
          enableCheckDepositPaymentMethod: context.settings.enableCheckDepositPaymentMethod,
          logoFileUrl: context.settings.logoFileUrl,
        }));
      } else {
        setInvoiceCustomData((prevState) => ({
          ...prevState,
          brandColor: defColor.color,
          liliLogoId: defLogo.id,
          templateId: defTemplate.id,
          templateVersion: defTemplate.version,
        }));
      }
      const intStep = getInitialStepNumberFromRoutePath(context.initialObStep);
      if (intStep) {
        setActiveStep(intStep);
      } else setActiveStep(ONBOARDING_VIEWS_MAP.LOGO);
    },
  });

  const { mutateAsync: updateInvoiceSettings, isLoading: isLoadingUpdateInvoiceSettings } =
    useUpdateInvoiceSettingsMutation({
      customerId,
      invoiceCustomData,
      customLogoExternalId,
      isEditMode: !!context.initialObStep,
    });

  /** Handle all steps submits * */
  const stepsSubmitActions = {
    [ONBOARDING_VIEWS_MAP.INTRO]: () => {
      fetchCustomizationData();
    },
    [ONBOARDING_VIEWS_MAP.LOGO]: async ({ type, data }: { type: string; data: any }) => {
      if (type === SUBMIT_TYPES.LOGO.LILI_LOGO_ID) {
        const selectedLogo = customizationData?.liliLogos.filter((logo) => logo.id === data[0])[0];
        setInvoiceCustomData((prevState) => ({
          ...prevState,
          liliLogoId: selectedLogo?.id || '',
          logoFileId: '',
          logoFileUrl: '',
        }));
      } else if (type === SUBMIT_TYPES.LOGO.LOGO_FILE_ID) {
        setCustomLogoExternalId(data?.externalId);
        setInvoiceCustomData((prevState) => ({
          ...prevState,
          logoFileUrl: data?.url,
          logoFileId: data?.externalId,
          liliLogoId: '',
        }));
      } else {
        updateInvoiceSettings().then((res) => {
          context?.updateSettings(res);
          if (context.initialObStep) context.clearInitialStep();
          else setActiveStep(ONBOARDING_VIEWS_MAP.COLOR);
        });
      }
    },
    [ONBOARDING_VIEWS_MAP.COLOR]: async ({ type, data }: { type: string; data: any }) => {
      if (type === SUBMIT_TYPES.COLOR.PICKED_COLOR) {
        setInvoiceCustomData((prevState) => ({ ...prevState, brandColor: data.replace('#', '') }));
      } else if (type === SUBMIT_TYPES.COLOR.NAVIGATE_BACK) {
        if (context.initialObStep) {
          context.clearInitialStep();
        } else {
          setActiveStep(STEPS_DATA.PAGES.COLOR.STEP_BACK_DESTINATION);
        }
      } else {
        updateInvoiceSettings().then((res) => {
          context?.updateSettings(res);
          if (context.initialObStep) context.clearInitialStep();
          else setActiveStep(ONBOARDING_VIEWS_MAP.DETAILS);
        });
      }
    },
    [ONBOARDING_VIEWS_MAP.DETAILS]: async ({ type, data }: { type: string; data: any }) => {
      if (type === SUBMIT_TYPES.DETAILS.CHECK_DETAILS) {
        setInvoiceCustomData((prevState) => ({ ...prevState, enableCheckDepositPaymentMethod: data }));
      } else if (type === SUBMIT_TYPES.DETAILS.ACC_DETAILS) {
        setInvoiceCustomData((prevState) => ({ ...prevState, enableAchPaymentMethod: data }));
      } else if (type === SUBMIT_TYPES.DETAILS.NAVIGATE_BACK) {
        if (context.initialObStep) {
          context.clearInitialStep();
        } else {
          setActiveStep(STEPS_DATA.PAGES.DETAILS.STEP_BACK_DESTINATION);
        }
      } else {
        updateInvoiceSettings().then((res) => {
          context?.updateSettings(res);
          if (context.initialObStep) context.clearInitialStep();
          else setActiveStep(ONBOARDING_VIEWS_MAP.PAYMENTS);
        });
      }
    },
    [ONBOARDING_VIEWS_MAP.PAYMENTS]: async ({ type, data }: { type: string; data: any }) => {
      if (type === SUBMIT_TYPES.PAYMENTS.NAVIGATE_BACK) {
        if (context.initialObStep) {
          context.clearInitialStep();
        } else {
          setActiveStep(STEPS_DATA.PAGES.PAYMENTS.STEP_BACK_DESTINATION);
        }
      } else if (type === SUBMIT_TYPES.PAYMENTS.CONNECTION_SUCCESS) {
        setConnectionType('');
        setConnectionType(data?.toLowerCase());
        setTimeout(() => {
          setConnectionType('');
        }, 3500);
      } else if (type === SUBMIT_TYPES.PAYMENTS.STRIPE_REDIRECT) {
        // setPaymentIFrameUrl(data);
        window.open(data, '_self');
      } else if (type === SUBMIT_TYPES.PAYMENTS.FINISH_CUSTOMIZATION) {
        setInvoiceCustomData((prevState) => ({ ...prevState, finishCustomization: true }));
      }
    },
  };

  const handleStepSubmit = (data?: any) => {
    stepsSubmitActions[activeStep as keyof typeof stepsSubmitActions](data);
  };

  const getLogoName = (id: string) => {
    const logoList = customizationData?.liliLogos;
    const filteredLogo = logoList?.filter((logo) => logo.id === id)[0];
    if (filteredLogo) {
      return filteredLogo.name;
    }
    return 'cooking1';
  };

  const isLoadingState = useMemo(() => {
    const loaders = [isRefetchCustomizationData, isInitialRefetchCustomizationData, isLoadingUpdateInvoiceSettings];
    return loaders.some((item) => item === true);
  }, [isRefetchCustomizationData, isInitialRefetchCustomizationData, isLoadingUpdateInvoiceSettings]);

  const iFrameUrl = useMemo(() => {
    const baseUrl = new URL(iFrameQueryParams.BASE_URL);
    baseUrl.searchParams.set(iFrameQueryParams.CUSTOMIZATION_INVOICE, 'true');
    baseUrl.searchParams.set(iFrameQueryParams.ORIGIN, 'webApp');
    baseUrl.searchParams.set(iFrameQueryParams.BRAND_COLOR, invoiceCustomData.brandColor);
    baseUrl.searchParams.set(
      iFrameQueryParams.LOGO_URL,
      `https://assets.lili.co/unified/invoice-logos/${getLogoName(invoiceCustomData.liliLogoId)}.svg`,
    );
    baseUrl.searchParams.set(iFrameQueryParams.CUSTOM_LOGO_PATH, invoiceCustomData.logoFileUrl || '');
    baseUrl.searchParams.set(
      iFrameQueryParams.SHOW_BANK_ACCOUNT,
      JSON.stringify(invoiceCustomData.enableAchPaymentMethod),
    );
    baseUrl.searchParams.set(
      iFrameQueryParams.SHOW_CHECK_DETAILS,
      JSON.stringify(invoiceCustomData.enableCheckDepositPaymentMethod),
    );
    return baseUrl;
  }, [invoiceCustomData]);

  const contextProvider: IOnboardingContext = useMemo(
    () => ({
      onStepSubmit: handleStepSubmit,
      activeStep,
      customizationData,
      invoiceCustomData,
      isLoadingState,
      featurePermission: invoicesPermission,
    }),
    [activeStep, customizationData, isLoadingState, invoiceCustomData, invoicesPermission],
  );

  const getDynamicNavStyles = () => {
    switch (activeStep) {
      case ONBOARDING_VIEWS_MAP.INTRO:
        return STYLES.HIDDEN;
      case ONBOARDING_VIEWS_MAP.LOGO:
      case ONBOARDING_VIEWS_MAP.COLOR:
      case ONBOARDING_VIEWS_MAP.DETAILS:
      case ONBOARDING_VIEWS_MAP.PAYMENTS:
        return STYLES.STEP_NAV_STYLE;
      default:
        return STYLES.HIDDEN;
    }
  };

  useEffect(() => {
    if (invoiceCustomData.finishCustomization) {
      updateInvoiceSettings().then((res) => {
        context?.updateSettings(res);
        context.onFinishOnboarding();
      });
    }
  }, [invoiceCustomData]);

  useEffect(() => {
    if (context?.settings?.logoFileUrl && context?.settings?.logoFileId) {
      setCustomLogoExternalId(context?.settings?.logoFileId);
      setInvoiceCustomData((prevState) => ({
        ...prevState,
        logoFileUrl: context?.settings?.logoFileUrl,
        logoFileId: context?.settings?.logoFileId,
        liliLogoId: '',
      }));
    }
  }, [context?.settings]);

  useEffect(() => {
    if (
      invoicesPermission !== FeatureAccess.READ_WRITE &&
      (activeStep !== ONBOARDING_VIEWS_MAP.INTRO || activeStep !== ONBOARDING_VIEWS_MAP.PRELOAD)
    ) {
      setActiveStep(ONBOARDING_VIEWS_MAP.INTRO);
    }
  }, [activeStep]);

  useEffect(() => {
    if (searchParams) {
      const rawParamsObject = new URLSearchParams(searchParams);
      const flowId = rawParamsObject.get('flow_id');
      const status = rawParamsObject.get('status');
      const paymentType = rawParamsObject.get('paymentOptionEnum');
      if (flowId === 'invoice_stripe' && status === 'success' && paymentType) {
        mutateCompleteStripeConnection({ customerId, type: paymentType }).then(() => {
          if (context.initialObStep && validateInitialStep(context.initialObStep)) {
            fetchCustomizationData();
          } else {
            setActiveStep(ONBOARDING_VIEWS_MAP.INTRO);
          }
        });
      }
    } else if (context.initialObStep && validateInitialStep(context.initialObStep)) {
      fetchCustomizationData();
    } else {
      setActiveStep(ONBOARDING_VIEWS_MAP.INTRO);
    }
  }, []);

  return (
    <AppLayout
      layoutStyle={STEPS_TO_AVOID_PROFILE_NAVBAR.includes(activeStep) ? STYLES.APP_LAYOUT_WIZARD : STYLES.APP_LAYOUT}
      navBarStyle={getDynamicNavStyles()}
      isWizardMode={STEPS_TO_AVOID_PROFILE_NAVBAR.includes(activeStep)}
      chatBotStyle='hidden'
      hideCustomerProfile
      navChildren={<NavChildren activeStep={activeStep} handleActiveStep={(stepNumber) => setActiveStep(stepNumber)} />}
    >
      <ContentWrapper styles={STYLES.CONTENT_WRAPPER}>
        <OnboardingContext.Provider value={contextProvider}>
          {connectionType && <SnackBar connectionType={connectionType} />}
          <StepSwitch activeStep={activeStep} />
          {shouldShowSubMenu && (
            <SubContent className={STYLES.SUB_CONTENT_CONTAINER}>
              <iframe className={STYLES.IFRAME} title='InvoicePreview' src={iFrameUrl.href} />
            </SubContent>
          )}
        </OnboardingContext.Provider>
      </ContentWrapper>
    </AppLayout>
  );
};
